import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import { auth as authApi, setAccessToken, type AuthUser } from '../lib/api'

interface AuthState {
  user: AuthUser | null
  isLoading: boolean
  isAuthenticated: boolean
}

interface AuthContextValue extends AuthState {
  signIn: (email: string, password: string) => Promise<void>
  signUp: (name: string, email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    isLoading: true,
    isAuthenticated: false,
  })

  // On mount, try to restore session via refresh token cookie
  useEffect(() => {
    authApi
      .me()
      .then(({ user }) => {
        setState({ user, isLoading: false, isAuthenticated: true })
      })
      .catch(() => {
        // No valid session — that's fine
        setState({ user: null, isLoading: false, isAuthenticated: false })
      })
  }, [])

  const signIn = useCallback(async (email: string, password: string) => {
    const { user, accessToken } = await authApi.signIn({ email, password })
    setAccessToken(accessToken)
    setState({ user, isLoading: false, isAuthenticated: true })
  }, [])

  const signUp = useCallback(async (name: string, email: string, password: string) => {
    const { user, accessToken } = await authApi.signUp({ name, email, password })
    setAccessToken(accessToken)
    setState({ user, isLoading: false, isAuthenticated: true })
  }, [])

  const signOut = useCallback(async () => {
    await authApi.signOut().catch(() => {})
    setAccessToken(null)
    setState({ user: null, isLoading: false, isAuthenticated: false })
  }, [])

  return (
    <AuthContext.Provider value={{ ...state, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}
